import React, { useState, useEffect } from 'react';
import { Mail, Phone, ArrowLeft, Eye, EyeOff, Key, CheckCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import Button from '../../common/Button/Button';
import { validateForm, validateField, VALIDATION_RULES, ErrorMessage } from '../../../utils/formValidation';
import authService from '../../../api/services/authService';

const ForgotPassword = ({ onBackToLogin }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    emailOrPhone: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    let timer;
    if (currentStep === 2 && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [currentStep, countdown]);

  const getValidationRules = (name) => {
    if (name === 'emailOrPhone') {
      return { ...VALIDATION_RULES.email, maxLength: 100, customLabel: 'Email' };
    }
    if (name === 'otp') return VALIDATION_RULES.otp;
    if (name === 'newPassword') return VALIDATION_RULES.password;
    if (name === 'confirmPassword') return { required: true };
    return {};
  };

  const validateFieldLevel = (name, value) => {
    const rules = getValidationRules(name);
    let fieldError = '';

    if (name === 'confirmPassword') {
      if (!value) {
        fieldError = 'Please confirm your new password';
      } else if (value !== formData.newPassword) {
        fieldError = 'Passwords do not match';
      }
    } else {
      const fieldErrors = validateField(name, value, rules);
      if (fieldErrors.length > 0) {
        fieldError = fieldErrors[0];
      }
    }

    setErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));

    return !fieldError;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateFieldLevel(name, value);
  };

  // Mutations
  const sendOTPMutation = useMutation({
    mutationFn: (data) => authService.sendOTP(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(response.message || 'OTP sent successfully!');
        setFormData(prev => ({ ...prev, otp: '' }));
        setCountdown(30);
        setCanResend(false);
        setCurrentStep(2);
      } else {
        toast.error(response.error || 'Failed to send OTP. Please try again.');
      }
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to send OTP. Please try again.');
    }
  });

  const verifyOTPMutation = useMutation({
    mutationFn: (data) => authService.verifyOTP(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(response.message || 'OTP verified successfully!');
        setCurrentStep(3);
      } else {
        toast.error(response.error || 'Invalid OTP. Please try again.');
      }
    },
    onError: (err) => {
      toast.error(err.message || 'Invalid OTP. Please try again.');
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (data) => authService.resetPassword(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(response.message || 'Password reset successfully!');
        setCurrentStep(4);
      } else {
        toast.error(response.error || 'Failed to reset password. Please try again.');
      }
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to reset password. Please try again.');
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'emailOrPhone') {
      // For email, limit to 100 characters
      const emailValue = value.slice(0, 100);
      setFormData(prev => ({ ...prev, [name]: emailValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    setErrors(prev => ({ ...prev, [name]: '' }));
  };


  const validateCurrentStep = () => {
    if (currentStep === 1) {
      return validateFieldLevel('emailOrPhone', formData.emailOrPhone);
    } else if (currentStep === 2) {
      return validateFieldLevel('otp', formData.otp);
    } else if (currentStep === 3) {
      const isNewValid = validateFieldLevel('newPassword', formData.newPassword);
      const isConfirmValid = validateFieldLevel('confirmPassword', formData.confirmPassword);
      return isNewValid && isConfirmValid;
    }
    return true;
  };

  const handleSendOTP = (e) => {
    if (e) e.preventDefault();
    const isValid = validateFieldLevel('emailOrPhone', formData.emailOrPhone);
    if (!isValid) {
      const fieldRules = getValidationRules('emailOrPhone');
      const errs = validateField('emailOrPhone', formData.emailOrPhone, fieldRules);
      const errMsg = errs.length > 0 ? errs[0] : 'Please enter a valid email address.';
      toast.error(errMsg, { id: 'auth-form-validation-error' });
      return;
    }
    toast.dismiss('auth-form-validation-error');
    sendOTPMutation.mutate({
      emailOrPhone: formData.emailOrPhone,
      method: 'email'
    });
  };

  const handleVerifyOTP = (e) => {
    e.preventDefault();
    const isValid = validateFieldLevel('otp', formData.otp);
    if (!isValid) {
      const fieldRules = getValidationRules('otp');
      const errs = validateField('otp', formData.otp, fieldRules);
      const errMsg = errs.length > 0 ? errs[0] : 'Please enter a valid 6-digit OTP code.';
      toast.error(errMsg, { id: 'auth-form-validation-error' });
      return;
    }
    toast.dismiss('auth-form-validation-error');
    verifyOTPMutation.mutate({
      emailOrPhone: formData.emailOrPhone,
      otp: formData.otp,
      method: 'email'
    });
  };

  const handleResetPassword = (e) => {
    e.preventDefault();
    const isNewValid = validateFieldLevel('newPassword', formData.newPassword);
    const isConfirmValid = validateFieldLevel('confirmPassword', formData.confirmPassword);
    if (!isNewValid || !isConfirmValid) {
      toast.error(errors.newPassword || errors.confirmPassword || 'Please enter valid passwords.', { id: 'auth-form-validation-error' });
      return;
    }
    toast.dismiss('auth-form-validation-error');
    resetPasswordMutation.mutate({
      emailOrPhone: formData.emailOrPhone,
      otp: formData.otp,
      newPassword: formData.newPassword,
      method: 'email'
    });
  };




  const LogoHeader = () => (
    <div className="flex items-center gap-3.5 mb-7">
      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
        <img 
          src="/mobiloitte-logo.png" 
          alt="Mobiloitte Logo" 
          className="w-full h-full object-contain"
        />
      </div>
      <div className="flex flex-col text-left">
        <h2 className="text-xl font-bold text-hrms-primary tracking-tight leading-none mb-1">
          Mobiloitte
        </h2>
        <p className="text-[10px] font-bold text-hrms-muted tracking-[0.2em] uppercase leading-none">
          Technologies
        </p>
      </div>
    </div>
  );

  if (currentStep === 1) {
    return (
      <div className="w-full font-sans py-2">
        <LogoHeader />
        
        <div className="mb-1">
          <h1 className="text-2xl lg:text-[28px] font-bold text-hrms-text leading-tight tracking-tight">
            Forgot Password?
          </h1>
        </div>
        <p className="text-hrms-muted text-[13px] leading-relaxed mb-5 max-w-sm">
          Enter your registered work email address below to receive password reset instructions.
        </p>

        <form noValidate onSubmit={handleSendOTP} className="space-y-4">
          <div>
            <label htmlFor="emailOrPhone" className="block text-sm font-medium text-hrms-text mb-1.5">
              Email Address
            </label>
            <div className="relative group">
              <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-blue-600 transition-colors pointer-events-none" />
              <input
                type="email"
                id="emailOrPhone"
                name="emailOrPhone"
                value={formData.emailOrPhone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                maxLength={100}
                className={`login-input ${errors.emailOrPhone ? 'login-input-error' : ''}`}
                placeholder="Enter your email address"
              />
            </div>
            {errors.emailOrPhone && (
              <p className="mt-1 text-xs text-red-500 font-sans">{errors.emailOrPhone}</p>
            )}
          </div>

          {/* Security Information Box */}
          <div className="p-3.5 rounded-xl bg-blue-50/70 border border-blue-100/80 flex items-start gap-2.5">
            <div className="w-2 h-2 rounded-full bg-blue-600 shrink-0 mt-1.5" />
            <p className="text-xs text-blue-900/80 font-sans leading-relaxed">
              For security reasons, verification codes expire after 10 minutes. Check your spam folder if you don't see it.
            </p>
          </div>

          <button
            type="submit"
            className="login-btn-primary mt-2"
            disabled={sendOTPMutation.isPending}
          >
            {sendOTPMutation.isPending ? 'Sending Code...' : 'Send Verification Code'}
          </button>
        </form>

        <div className="mt-6 pt-4 border-t border-gray-100 text-center">
          <button
            type="button"
            onClick={onBackToLogin}
            className="inline-flex items-center text-sm text-hrms-primary hover:text-[#0B2033] font-medium transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (currentStep === 2) {
    return (
      <div className="w-full font-sans">
        <LogoHeader />
        
        <div className="mb-1">
          <h1 className="text-2xl lg:text-[28px] font-bold text-hrms-text leading-tight tracking-tight">
            Verify Code
          </h1>
        </div>
        <p className="text-hrms-muted text-[13px] leading-relaxed mb-6 max-w-xs">
          We've sent a code to <span className="text-hrms-primary font-medium">{formData.emailOrPhone}</span>
        </p>

        <form onSubmit={handleVerifyOTP} className="space-y-4">
          <div>
            <label htmlFor="otp" className="block text-sm font-medium text-hrms-text mb-1.5">
              Enter 6-digit code
            </label>
            <div className="relative">
              <Key className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                id="otp"
                name="otp"
                value={formData.otp}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`login-input tracking-[0.5em] text-center font-bold ${errors.otp ? 'login-input-error' : ''}`}
                placeholder="••••••"
                maxLength="6"
              />
            </div>
            {errors.otp && (
              <p className="mt-1 text-xs text-red-500 font-sans text-center">{errors.otp}</p>
            )}
          </div>

          <button
            type="submit"
            className="login-btn-primary mt-2"
            disabled={verifyOTPMutation.isPending}
          >
            {verifyOTPMutation.isPending ? 'Verifying...' : 'Verify'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => {
              if (canResend && !sendOTPMutation.isPending) {
                handleSendOTP();
              }
            }}
            disabled={!canResend || sendOTPMutation.isPending}
            className={`text-sm font-medium transition-colors ${canResend
              ? 'text-hrms-primary hover:text-[#0B2033] cursor-pointer'
              : 'text-gray-400 cursor-not-allowed'
              }`}
          >
            {sendOTPMutation.isPending
              ? 'Sending...'
              : canResend
                ? 'Resend Code'
                : `Resend Code in ${countdown}s`
            }
          </button>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-100 text-center">
          <button
            type="button"
            onClick={() => {
              setFormData(prev => ({ ...prev, otp: '' }));
              setErrors({});
              setCurrentStep(1);
            }}
            className="inline-flex items-center text-sm text-hrms-muted hover:text-hrms-text transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Change email
          </button>
        </div>
      </div>
    );
  }

  if (currentStep === 3) {
    return (
      <div className="w-full font-sans">
        <LogoHeader />
        
        <div className="mb-1">
          <h1 className="text-2xl lg:text-[28px] font-bold text-hrms-text leading-tight tracking-tight">
            New Password
          </h1>
        </div>
        <p className="text-hrms-muted text-[13px] leading-relaxed mb-6 max-w-xs">
          Please create a strong password for your account.
        </p>

        <form onSubmit={handleResetPassword} className="space-y-4">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-hrms-text mb-1.5">
              New Password
            </label>
            <div className="relative">
              <Key className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`login-input pr-10 ${errors.newPassword ? 'login-input-error' : ''}`}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.newPassword && (
              <p className="mt-1 text-xs text-red-500 font-sans">{errors.newPassword}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-hrms-text mb-1.5">
              Confirm New Password
            </label>
            <div className="relative">
              <Key className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`login-input pr-10 ${errors.confirmPassword ? 'login-input-error' : ''}`}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-xs text-red-500 font-sans">{errors.confirmPassword}</p>
            )}
          </div>

          <button
            type="submit"
            className="login-btn-primary mt-2"
            disabled={resetPasswordMutation.isPending}
          >
            {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="mt-6 pt-4 border-t border-gray-100 text-center">
          <button
            type="button"
            onClick={onBackToLogin}
            className="inline-flex items-center text-sm text-hrms-muted hover:text-hrms-text transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (currentStep === 4) {
    return (
      <div className="w-full font-sans">
        <LogoHeader />
        
        <div className="text-center mb-6">
          <div className="mx-auto mb-6 w-16 h-16 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center shadow-sm">
            <CheckCircle className="h-8 w-8 text-hrms-primary" />
          </div>
          <h2 className="text-2xl lg:text-[28px] font-bold text-hrms-text leading-tight tracking-tight mb-2">
            Reset Successful
          </h2>
          <p className="text-hrms-muted text-[13px] leading-relaxed max-w-xs mx-auto">
            Your password has been successfully reset. You can now login with your new password.
          </p>
        </div>

        <div className="space-y-4">
          <button
            type="button"
            className="login-btn-primary"
            onClick={onBackToLogin}
          >
            Continue to Login
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default ForgotPassword;
